import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
    FiLogOut, FiUser, FiHome, FiBriefcase, FiUsers, FiSettings,
    FiMenu, FiX, FiChevronDown, FiFileText, FiTruck, FiDollarSign,
    FiEdit3, FiBarChart2, FiCalendar,
    FiCreditCard,
    FiPieChart,
    FiGrid,
    FiMessageSquare, FiBell,
    FiCheck,
    FiCheckCircle,
    FiSlash
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import MessageMenu from '../messaging/MessageMenu';
import NotificationMenu from '../notification/NotificationMenu';

import { useMessageNotifications } from '../../hooks/useMessageNotifications';
import { useNotificationBadge } from '../../hooks/useNotificationBadge';
import { TiTick } from "react-icons/ti";

const Navbar = ({ isSidebarOpen, setIsSidebarOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, company, signOut, loading } = useAuth();
    const [isOpen, setIsOpen] = useState(false);

    const [showMessageMenu, setShowMessageMenu] = useState(false);
    const [showNotificationMenu, setShowNotificationMenu] = useState(false);

    const { unreadCount: unreadMessageCount, refreshCount: refreshMessageBadge } = useMessageNotifications(userProfile?.id);
    const { unreadCount: unreadNotificationCount, refreshCount: refreshNotificationBadge } = useNotificationBadge(userProfile?.id);

    const companySlug = company?.slug;


    // Navigation items based on user role
    const getNavigationItems = () => {
        const commonItems = [
            { name: 'Dashboard', path: `/${companySlug}/dashboard`, icon: FiHome },
        ];

        const adminItems = [
            ...commonItems,
            { name: 'Assignments', path: `/${companySlug}/assignment/list`, icon: FiFileText },
            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Users', path: `/${companySlug}/users`, icon: FiUsers },
            { name: 'Analytics', path: `./${companySlug}/analytics`, icon: FiBarChart2, disabled: true },
            { name: 'Settings', path: `/${companySlug}/settings`, icon: FiSettings, disabled: true },
        ];

        const designerItems = [
            ...commonItems,
            { name: 'Assignments', path: `/${companySlug}/assignment/list`, icon: FiFileText },
            { name: 'My Assignments', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 },
            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Users', path: `/${companySlug}/users`, icon: FiUsers },

        ];

        const estimatorItems = [
            ...commonItems,
            { name: 'Assignments', path: `/${companySlug}/assignment/list`, icon: FiFileText },
            { name: 'My Assignments', path: `/${companySlug}/assignment/my-assignments`, icon: FiEdit3 },
            { name: 'Schedule', path: `/${companySlug}/schedule`, icon: FiCalendar },
            { name: 'Users', path: `/${companySlug}/users`, icon: FiUsers },

        ];

        const driverItems = [
            ...commonItems,
            { name: 'My Deliveries', path: `/${companySlug}/my-deliveries`, icon: FiTruck },
            { name: 'Completed Deliveries', path: `/${companySlug}/my-deliveries/completed`, icon: FiCheckCircle },
            { name: 'Users', path: `/${companySlug}/users`, icon: FiUsers },

        ];

        const appOwnerItems = [
            { name: 'Dashboard', path: `/${companySlug}/dashboard`, icon: FiHome },
            { name: 'Manage Companies', path: `/${companySlug}/companies`, icon: FiGrid },
            { name: 'Manage Users', path: `/${companySlug}/users`, icon: FiUsers },
            { name: 'System Analytics', path: `/${companySlug}/analytics`, icon: FiPieChart },
            { name: 'Activity Logs', path: `/${companySlug}/activity`, icon: FiFileText },
            { name: 'Billing & Plans', path: `/${companySlug}/billing`, icon: FiCreditCard },
        ]

        if (userProfile?.status !== "active") return [
            { name: `Account ${userProfile?.status}`, path: ``, icon: FiSlash, disabled: true },
        ]

        if (companySlug === "notify") return appOwnerItems;

        switch (userProfile?.role) {
            case 'super_admin':
            case 'admin':
                return adminItems;
            case 'designer':
                return designerItems;
            case 'estimator':
                return estimatorItems;
            case 'driver':
                return driverItems;
            default:
                return commonItems;
        }
    };

    const navigationItems = getNavigationItems();

    // Get user type styling
    const getUserTypeStyle = () => {
        switch (userProfile?.role) {
            case 'super_admin':
            case 'admin':
                return {
                    bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
                    text: 'text-blue-600',
                    lightBg: 'bg-blue-50',
                    darkBg: 'bg-blue-900/20',
                };
            case 'designer':
                return {
                    bg: 'bg-gradient-to-br from-green-500 to-green-600',
                    text: 'text-green-600',
                    lightBg: 'bg-green-50',
                    darkBg: 'bg-green-900/20',
                };
            case 'estimator':
                return {
                    bg: 'bg-gradient-to-br from-purple-500 to-purple-600',
                    text: 'text-purple-600',
                    lightBg: 'bg-purple-50',
                    darkBg: 'bg-purple-900/20',
                };
            case 'driver':
                return {
                    bg: 'bg-gradient-to-br from-red-500 to-red-600',
                    text: 'text-red-600',
                    lightBg: 'bg-red-50',
                    darkBg: 'bg-red-900/20',
                };
            case 'app_owner':
                return {
                    bg: 'bg-gradient-to-br from-rose-500 to-rose-600',
                    text: 'text-rose-600',
                    lightBg: 'bg-rose-50',
                    darkBg: 'bg-rose-900/20',
                };
            default:
                return {
                    bg: 'bg-gradient-to-br from-gray-500 to-gray-600',
                    text: 'text-gray-600',
                    lightBg: 'bg-gray-50',
                    darkBg: 'bg-gray-900/20',
                };
        }
    };

    const userStyle = getUserTypeStyle();

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const isActivePath = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    // Show skeleton navbar while loading
    if (loading) {
        return (
            <>
                <aside
                    className={`fixed top-0 left-0 z-50 h-screen bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-20'}`}
                >
                    <div className="flex flex-col h-full">
                        {/* Logo Section Skeleton */}
                        <div className="flex items-center justify-center h-20 px-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                            {isSidebarOpen ? (
                                <div className="flex items-center space-x-3 w-full">
                                    <Skeleton circle width={40} height={40} />
                                    <Skeleton width={100} height={24} />
                                </div>
                            ) : (
                                <Skeleton circle width={24} height={24} />
                            )}
                        </div>

                        {/* Company Info Skeleton */}
                        <div className="px-4 py-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                            <div className={`flex items-center ${!isSidebarOpen ? 'justify-center' : 'space-x-3'}`}>
                                <Skeleton circle width={32} height={32} />
                                {isSidebarOpen && (
                                    <div className="flex-1">
                                        <Skeleton width={120} height={16} className="mb-1" />
                                        <Skeleton width={80} height={12} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Navigation Links Skeleton */}
                        <div className="flex-1 overflow-y-auto py-4 px-3">
                            <div className="space-y-1">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className={`flex items-center ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto' : 'h-11 px-3 space-x-3'}`}>
                                        <Skeleton circle={!isSidebarOpen} width={isSidebarOpen ? 20 : 25} height={isSidebarOpen ? 20 : 25} />
                                        {isSidebarOpen && <Skeleton width={100} height={16} />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Messages & Notifications Skeleton */}
                        <div className="border-t border-gray-200 dark:border-zinc-800 pt-2 space-y-1 px-3">
                            {[...Array(2)].map((_, index) => (
                                <div key={index} className={`flex items-center ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto' : 'h-11 px-3 space-x-3'}`}>
                                    <Skeleton circle={!isSidebarOpen} width={isSidebarOpen ? 20 : 25} height={isSidebarOpen ? 20 : 25} />
                                    {isSidebarOpen && <Skeleton width={80} height={16} />}
                                </div>
                            ))}
                        </div>

                        {/* User Profile Skeleton */}
                        <div className="border-t border-gray-200 dark:border-zinc-800 p-4 space-y-2 flex-shrink-0">
                            <div className={`flex flex-col items-center ${!isSidebarOpen ? 'justify-center' : 'space-x-3'}`}>
                                <Skeleton circle width={40} height={40} />
                                <Skeleton circle width={40} height={40} />
                                {isSidebarOpen && (
                                    <div className="flex-1">
                                        <Skeleton width={120} height={16} className="mb-1" />
                                        <Skeleton width={80} height={12} />
                                    </div>
                                )}
                            </div>
                            {isSidebarOpen && (
                                <Skeleton width="100%" height={44} borderRadius={12} />
                            )}
                        </div>
                    </div>
                </aside>
            </>
        );
    }

    return (
        <>
            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-50 h-screen bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-20'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Section 1 - Logo & Toggle */}
                    <div className="flex items-center justify-center h-20 px-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                        {isSidebarOpen ? (
                            <>
                                <a href="https://notifyapp.com.au" target="_blank" rel="noopener noreferrer">
                                    <div className="flex items-center space-x-3 overflow-hidden transition-opacity duration-200 w-36">
                                        {/* <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-bold text-xl">N</span>
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
                                        Notify
                                    </span> */}
                                        <img className="-ml-2" src={process.env.PUBLIC_URL + '/assets/notify-cropped-wide.png'} alt="" />
                                    </div>
                                </a>
                                <button
                                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0 ml-auto"
                                >
                                    <FiX className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                            >
                                <FiMenu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                        )}
                    </div>

                    {/* Section 2 - Company Info */}
                    <div className="px-4 py-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
                        <button
                            className={`w-full flex items-center rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all group ${!isSidebarOpen ? 'justify-center h-12 w-12 mx-auto p-0' : 'h-14 px-3 space-x-3'
                                }`}
                            title={!isSidebarOpen ? "Click to change company" : ""}
                        >
                            {company?.logo_url ? (
                                <img
                                    src={company.logo_url}
                                    alt={company.name}
                                    className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                                />
                            ) : (
                                <div className={`w-8 h-8 ${userStyle.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                    <span className="text-white font-semibold text-sm">
                                        {company?.name?.charAt(0) || 'C'}
                                    </span>
                                </div>
                            )}
                            <div className={`flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                                }`}>
                                <div className="font-semibold text-gray-900 dark:text-white truncate text-sm">
                                    {company?.name || 'Company'}
                                </div>
                                {/* <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                                    Click to change company
                                </div> */}
                            </div>
                            {/* {isSidebarOpen && (
                                <FiChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )} */}
                        </button>
                    </div>

                    {/* Section 3 - Navigation Links (Scrollable) */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-zinc-700 scrollbar-track-transparent">
                        <nav className="space-y-1">
                            {navigationItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = isActivePath(item.path);

                                return (
                                    <button
                                        key={item.path}
                                        onClick={() => navigate(item.path)}
                                        className={`w-full flex items-center rounded-xl transition-all group
                                            ${item?.disabled && "opacity-60 cursor-not-allowed"}
                                             ${isActive
                                                ? `${userStyle.lightBg} dark:${userStyle.darkBg} ${userStyle.text}`
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                            } ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto p-0' : 'h-11 px-3 space-x-3'}`}
                                        title={!isSidebarOpen ? item.name : ''}
                                        disabled={item?.disabled && true}
                                    >
                                        <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? userStyle.text : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                                            }`} />
                                        <span className={`font-medium truncate transition-all duration-300 text-sm ${isActive ? 'font-semibold' : ''
                                            } ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 w-0'}`}>
                                            {item.name}
                                        </span>
                                        {isSidebarOpen && isActive && (
                                            <div className={`ml-auto w-1.5 h-1.5 rounded-full ${userStyle.bg} flex-shrink-0`}></div>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Section 3.5 - Messages & Notifications */}
                    <div className="border-t border-gray-200 dark:border-zinc-800 pt-2 my-2 space-y-1">
                        {/* Messages Button */}
                        <button
                            onClick={() => {
                                if (userProfile?.status === "active") {
                                    setShowMessageMenu(!showMessageMenu);
                                    setShowNotificationMenu(false);
                                }
                            }}
                            className={`w-full flex items-center rounded-xl transition-all group relative ${showMessageMenu
                                ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                } ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto p-0' : 'h-11 px-3 space-x-3'} ${userProfile?.status !== "active" && "cursor-not-allowed"}`}
                            title={!isSidebarOpen ? 'Messages' : ''}
                            disabled={userProfile?.status !== "active"}
                        >
                            <div className="relative">
                                <FiMessageSquare className="w-5 h-5 flex-shrink-0" />
                                {unreadMessageCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                        {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                    </span>
                                )}
                            </div>
                            <span className={`font-medium truncate transition-all duration-300 text-sm ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 w-0'
                                }`}>
                                Messages
                            </span>
                            {isSidebarOpen && unreadMessageCount > 0 && (
                                <span className="ml-auto px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-bold">
                                    {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                                </span>
                            )}
                        </button>

                        {/* Notifications Button */}
                        <button
                            onClick={() => {
                                if (userProfile?.status === "active") {
                                    setShowNotificationMenu(!showNotificationMenu);
                                    setShowMessageMenu(false);
                                }
                            }}
                            className={`w-full flex items-center rounded-xl transition-all group relative ${showNotificationMenu
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                                } ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto p-0' : 'h-11 px-3 space-x-3'} ${userProfile?.status !== "active" && "cursor-not-allowed"}`}
                            title={!isSidebarOpen ? 'Notifications' : ''}
                            disabled={userProfile?.status !== "active"}
                        >
                            <div className="relative">
                                <FiBell className="w-5 h-5 flex-shrink-0" />
                                {unreadNotificationCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                        {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                    </span>
                                )}
                            </div>
                            <span className={`font-medium truncate transition-all duration-300 text-sm ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 w-0'
                                }`}>
                                Notifications
                            </span>
                            {isSidebarOpen && unreadNotificationCount > 0 && (
                                <span className="ml-auto px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold">
                                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                                </span>
                            )}
                        </button>
                    </div>


                    {/* Section 4 - User Profile & Logout */}
                    <div className="border-t border-gray-200 dark:border-zinc-800 p-4 space-y-2 flex-shrink-0">

                        {!isSidebarOpen &&
                            <a href="https://notifyapp.com.au" target="_blank" rel="noopener noreferrer">
                                <img className="w-11 align-middle justify-center self-center object-center place-self-center" src={process.env.PUBLIC_URL + '/assets/icon.png'} alt="" />
                            </a>
                        }

                        {/* User Profile */}
                        <button
                            onClick={() => { }} // navigate(`/${companySlug}/user/${userProfile?.username?.toLowerCase() || userProfile?.id}`)
                            className={`w-full flex items-center rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all ${!isSidebarOpen ? 'justify-center h-14 w-14 mx-auto p-0' : 'h-14 px-3 space-x-3'
                                }`}
                        >
                            <div className={`w-10 h-10 ${userStyle.bg} rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${userProfile?.status !== "active" && "bg-gradient-to-br from-gray-500 to-gray-600"}`}>
                                {userProfile?.avatar_url ? (
                                    <img
                                        src={userProfile.avatar_url}
                                        alt={userProfile.full_name || userProfile.username}
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                ) : (
                                    <FiUser className="w-5 h-5 text-white" />
                                )}
                            </div>
                            <div className={`flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
                                }`}>
                                <div className="font-semibold text-gray-900 dark:text-white truncate text-sm">
                                    {userProfile?.full_name || userProfile?.username}
                                </div>
                                <div className={`text-xs font-medium ${userStyle.text} uppercase truncate ${userProfile?.status !== "active" && "text-gray-500"}`}>
                                    {userProfile?.status !== "active" ?
                                        userProfile?.status
                                        :
                                        userProfile?.role?.split("_")?.join(" ") || "USER"
                                    }
                                </div>
                            </div>
                        </button>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className={`w-full flex items-center rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all ${!isSidebarOpen ? 'justify-center h-11 w-11 mx-auto p-0' : 'h-11 px-3 space-x-3'
                                }`}
                            title={!isSidebarOpen ? 'Sign Out' : ''}
                        >
                            <FiLogOut className="w-5 h-5 flex-shrink-0" />
                            <span className={`font-medium text-sm transition-all duration-300 whitespace-nowrap ${isSidebarOpen ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 w-0'
                                }`}>
                                Sign Out
                            </span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Message and Notification Menus */}
            <MessageMenu
                isOpen={showMessageMenu}
                onClose={() => setShowMessageMenu(false)}
                onRefreshBadge={refreshMessageBadge}
                isSidebarOpen={isSidebarOpen}
            />
            <NotificationMenu
                isOpen={showNotificationMenu}
                onClose={() => setShowNotificationMenu(false)}
                onRefreshBadge={refreshNotificationBadge}
                isSidebarOpen={isSidebarOpen}
            />


            {/* Mobile overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
        </>
    );
};

export default Navbar;